import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

export const SLIDESHOW_KEY = "a2905267-caae-4d43-bd41-831f4ca77a85";
const MANIFEST_URL = "https://d381hui2s4gftp.cloudfront.net/wedding/media/manifest.json";
const HUD_HIDE_MS = 2500;
const AUTOPLAY_MS = 5000;

const parseIndexFromHash = (length) => {
    if (typeof window === "undefined") return 0;
    const raw = window.location.hash.replace(/^#/, "");
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || length === 0) return 0;
    return ((n % length) + length) % length;
};

export const Slideshow = ({shouldPlay, setShouldPlay}) => {
    const {key} = useParams();
    const navigate = useNavigate();

    const [photos, setPhotos] = useState([]);
    const [index, setIndex] = useState(0);
    const [error, setError] = useState(null);
    const [hudVisible, setHudVisible] = useState(true);
    const [playing, setPlaying] = useState(true);
    const [copied, setCopied] = useState(false);
    const [isTouch] = useState(() => typeof window !== "undefined" && window.matchMedia("(hover: none)").matches);
    const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
    const hudTimer = useRef(null);
    const copiedTimer = useRef(null);
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const swipedRef = useRef(false);

    const shareOrCopy = useCallback(async (e) => {
        e.stopPropagation();
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({url, title: "Wedding photos — Simone & Hunter"});
                return;
            } catch (err) {
                if (err && err.name === "AbortError") return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch {}
            document.body.removeChild(ta);
        }
        setCopied(true);
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 1800);
    }, []);

    const keyMatches = key === SLIDESHOW_KEY;

    useEffect(() => {
        if (!keyMatches) return;
        let cancelled = false;
        fetch(MANIFEST_URL)
            .then(r => {
                if (!r.ok) throw new Error(`Manifest fetch failed: ${r.status}`);
                return r.json();
            })
            .then(data => {
                if (cancelled) return;
                setPhotos(data);
                setIndex(parseIndexFromHash(data.length));
            })
            .catch(err => !cancelled && setError(err.message));
        return () => { cancelled = true; };
    }, [keyMatches]);

    useEffect(() => {
        if (photos.length === 0) return;
        window.history.replaceState(null, "", `#${index}`);
    }, [index, photos.length]);

    const showHud = useCallback(() => {
        setHudVisible(true);
        if (hudTimer.current) clearTimeout(hudTimer.current);
        hudTimer.current = setTimeout(() => setHudVisible(false), HUD_HIDE_MS);
    }, []);

    useEffect(() => {
        showHud();
        return () => { if (hudTimer.current) clearTimeout(hudTimer.current); };
    }, [showHud]);

    const advance = useCallback((delta) => {
        setPhotos(prev => prev); // no-op to keep closure honest
        setIndex(prev => {
            if (photos.length === 0) return prev;
            return ((prev + delta) % photos.length + photos.length) % photos.length;
        });
        showHud();
    }, [photos.length, showHud]);

    useEffect(() => {
        const onKey = (e) => {
            if (photos.length === 0) return;
            switch (e.key) {
                case "ArrowRight":
                    e.preventDefault();
                    advance(1);
                    break;
                case "ArrowLeft":
                    e.preventDefault();
                    advance(-1);
                    break;
                case " ":
                    e.preventDefault();
                    setPlaying(p => !p);
                    showHud();
                    break;
                case "Home":
                    e.preventDefault();
                    setIndex(0);
                    showHud();
                    break;
                case "End":
                    e.preventDefault();
                    setIndex(photos.length - 1);
                    showHud();
                    break;
                case "Escape":
                    navigate("/");
                    break;
                default:
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [advance, navigate, photos.length, showHud]);

    useEffect(() => {
        if (!playing || photos.length === 0) return;
        const id = setInterval(() => {
            setIndex(prev => (prev + 1) % photos.length);
        }, AUTOPLAY_MS);
        return () => clearInterval(id);
    }, [playing, photos.length, index]);

    if (!keyMatches) {
        return (
            <div style={{minHeight: "100vh", display: "grid", placeItems: "center", background: "#000", color: "#aaa", fontFamily: "system-ui"}}>
                Not found.
            </div>
        );
    }

    if (error) {
        return (
            <div style={{minHeight: "100vh", display: "grid", placeItems: "center", background: "#000", color: "#f88", fontFamily: "system-ui"}}>
                Failed to load photos: {error}
            </div>
        );
    }

    if (photos.length === 0) {
        return (
            <div style={{minHeight: "100vh", display: "grid", placeItems: "center", background: "#000", color: "#aaa", fontFamily: "system-ui"}}>
                Loading...
            </div>
        );
    }

    const current = photos[index];
    const next = photos[(index + 1) % photos.length];
    const prev = photos[(index - 1 + photos.length) % photos.length];

    const onTouchStart = (e) => {
        const t = e.touches[0];
        touchStartX.current = t.clientX;
        touchStartY.current = t.clientY;
        swipedRef.current = false;
    };

    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStartX.current;
        const dy = t.clientY - touchStartY.current;
        touchStartX.current = null;
        touchStartY.current = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            swipedRef.current = true;
            advance(dx < 0 ? 1 : -1);
        }
    };

    const onBgClick = () => {
        if (swipedRef.current) { swipedRef.current = false; return; }
        if (isTouch) {
            if (hudVisible) {
                if (hudTimer.current) clearTimeout(hudTimer.current);
                setHudVisible(false);
            } else {
                showHud();
            }
        } else {
            advance(1);
        }
    };

    const stop = (e) => e.stopPropagation();

    const ctrlBtnStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 48,
        height: 48,
        background: "rgba(0,0,0,0.45)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 999,
        cursor: "pointer",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        padding: 0,
    };

    return (
        <div
            onMouseMove={showHud}
            onClick={onBgClick}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{
                position: "fixed",
                inset: 0,
                background: "#000",
                cursor: hudVisible ? "default" : "none",
                overflow: "hidden",
                touchAction: "pan-y",
            }}
        >
            <img
                key={current.fullSrc}
                src={current.fullSrc}
                alt={current.filename}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                }}
                draggable={false}
            />
            <img src={next.fullSrc} alt="" style={{display: "none"}} />
            <img src={prev.fullSrc} alt="" style={{display: "none"}} />

            <div
                style={{
                    position: "absolute",
                    top: `calc(env(safe-area-inset-top, 0px) + 16px)`,
                    left: 16,
                    display: "flex",
                    gap: 8,
                    opacity: hudVisible ? 1 : 0,
                    transition: "opacity 400ms ease",
                    pointerEvents: hudVisible ? "auto" : "none",
                }}
            >
                <button
                    onClick={(e) => { stop(e); navigate("/"); }}
                    onTouchStart={stop}
                    onTouchEnd={stop}
                    aria-label="Close slideshow"
                    title="Close slideshow"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 14px",
                        minHeight: 44,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 999,
                        cursor: "pointer",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: 14,
                        backdropFilter: "blur(8px)",
                        WebkitBackdropFilter: "blur(8px)",
                        transition: "background 200ms ease",
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Close
                </button>
                <button
                    onClick={(e) => { stop(e); navigate(`/grid/${SLIDESHOW_KEY}#${index}`); }}
                    onTouchStart={stop}
                    onTouchEnd={stop}
                    aria-label="View all photos in grid"
                    title="View all photos in grid"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 14px",
                        minHeight: 44,
                        background: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 999,
                        cursor: "pointer",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: 14,
                        backdropFilter: "blur(8px)",
                        WebkitBackdropFilter: "blur(8px)",
                        transition: "background 200ms ease",
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"/>
                        <rect x="14" y="3" width="7" height="7"/>
                        <rect x="3" y="14" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/>
                    </svg>
                    All Photos
                </button>
            </div>

            <div
                style={{
                    position: "absolute",
                    top: `calc(env(safe-area-inset-top, 0px) + 16px)`,
                    right: 16,
                    display: "flex",
                    gap: 8,
                    opacity: hudVisible ? 1 : 0,
                    transition: "opacity 400ms ease",
                    pointerEvents: hudVisible ? "auto" : "none",
                }}
            >
            <a
                href={current.fullSrc}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stop}
                onTouchStart={stop}
                onTouchEnd={stop}
                aria-label="Open photo in a new window"
                title="Open photo in a new window"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    minHeight: 44,
                    background: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    textDecoration: "none",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    transition: "background 200ms ease",
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                Open Photo
            </a>
            <button
                onClick={shareOrCopy}
                onTouchStart={stop}
                onTouchEnd={stop}
                aria-label={canShare ? "Share this photo" : "Copy link to this photo"}
                title={canShare ? "Share this photo" : "Copy link to this photo"}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    minHeight: 44,
                    background: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: 14,
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    transition: "background 200ms ease",
                }}
            >
                {copied ? (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Copied!
                    </>
                ) : canShare ? (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 3v13"/>
                            <path d="M7 8l5-5 5 5"/>
                            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"/>
                        </svg>
                        Share
                    </>
                ) : (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                        Copy link
                    </>
                )}
            </button>
            </div>

            <div
                style={{
                    position: "absolute",
                    left: 0, right: 0,
                    bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)",
                    display: "flex",
                    justifyContent: "center",
                    gap: 16,
                    opacity: hudVisible ? 1 : 0,
                    transition: "opacity 400ms ease",
                    pointerEvents: hudVisible ? "auto" : "none",
                }}
                onClick={stop}
                onTouchStart={stop}
                onTouchEnd={stop}
            >
                <button onClick={(e) => { stop(e); advance(-1); }} aria-label="Previous photo" style={ctrlBtnStyle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                    </svg>
                </button>
                <button onClick={(e) => { stop(e); setPlaying(p => !p); showHud(); }} aria-label={playing ? "Pause" : "Play"} style={ctrlBtnStyle}>
                    {playing ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                            <rect x="6" y="5" width="4" height="14" rx="1"/>
                            <rect x="14" y="5" width="4" height="14" rx="1"/>
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                            <polygon points="7 5 19 12 7 19"/>
                        </svg>
                    )}
                </button>
                <button onClick={(e) => { stop(e); advance(1); }} aria-label="Next photo" style={ctrlBtnStyle}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </button>
                {setShouldPlay && (
                    <button onClick={(e) => { stop(e); setShouldPlay(p => !p); showHud(); }} aria-label={shouldPlay ? "Mute music" : "Play music"} style={ctrlBtnStyle}>
                        {shouldPlay ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="currentColor"/>
                                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="currentColor"/>
                                <line x1="22" y1="9" x2="16" y2="15"/>
                                <line x1="16" y1="9" x2="22" y2="15"/>
                            </svg>
                        )}
                    </button>
                )}
            </div>

            <div
                style={{
                    position: "absolute",
                    left: 0, right: 0, bottom: 0,
                    padding: `16px 24px calc(env(safe-area-inset-bottom, 0px) + 16px)`,
                    color: "#fff",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: 14,
                    background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))",
                    opacity: hudVisible ? 1 : 0,
                    transition: "opacity 400ms ease",
                    pointerEvents: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    gap: 12,
                }}
            >
                <div>
                    <div style={{fontWeight: 600}}>
                        {playing ? "▶" : "⏸"} {index + 1} / {photos.length}
                    </div>
                    <div style={{opacity: 0.85}}>
                        Photo by {current.firstName} {current.lastName}
                        {current.category ? ` · ${current.category}` : ""}
                    </div>
                </div>
                <div style={{opacity: 0.7, textAlign: "right", display: isTouch ? "none" : "block"}}>
                    ← / → navigate · Space play/pause · Esc exit
                </div>
            </div>
        </div>
    );
};

export default Slideshow;
